import { Link } from 'react-router';
import { GuideLayout, GuideSection, GuideTip } from '@/components/guides/guide-layout';
import { PhaseFiveGuideNotes } from '@/components/guides/phase-five-guide-notes';

const inlineLink =
  'font-medium text-brand-600 underline decoration-brand-300 underline-offset-2';

export function CouncilClaimOrCarInsuranceGuide() {
  return (
    <GuideLayout
      slug="council-claim-or-car-insurance"
      category="Decision guide"
      categoryColor="warning"
      title="Council Claim or Car Insurance?"
      seoTitle="Council Claim or Car Insurance? | Fixtyer"
      description="Compare a direct highway-authority claim with a motor-insurance claim, including timing, excess and no-claims considerations."
      readTime="7 min"
      updatedDate="2026-07-19"
      reviewedDate="2026-07-19"
      datePublished="2026-07-19"
      reviewedFor="England and Wales"
      contentOwner="Elevyn Technology Group Limited editorial team"
      sources={[
        {
          name: 'GOV.UK vehicle-damage claims',
          url: 'https://www.gov.uk/claim-for-damage-to-your-vehicle',
        },
        {
          name: 'MoneyHelper: no-claims bonus',
          url: 'https://www.moneyhelper.org.uk/en/blog/car-insurance/should-I-protect-my-no-claims-bonus',
        },
      ]}
      atAGlance={[
        {
          label: 'Answer first',
          value:
            'A direct highway-authority claim and a motor-insurance claim are different routes with different evidence, timing and financial consequences.',
        },
        {
          label: 'Check first',
          value:
            'Read your policy’s notification, cover, excess and no-claims terms, then speak to your insurer about your actual policy.',
        },
        {
          label: 'Direct claim',
          value:
            'The authority investigates legal liability; damage caused by a road does not create automatic compensation.',
        },
      ]}
      relatedGuides={[
        {
          slug: 'highway-authority-claim-routes',
          title: 'Claim Routes Directory',
          readTime: '6 min',
        },
        {
          slug: 'documenting-vehicle-damage',
          title: 'Document Vehicle Damage',
          readTime: '5 min',
        },
        {
          slug: 'pothole-claim-rejected-next-steps',
          title: 'Rejection Next Steps',
          readTime: '8 min',
        },
      ]}
    >
      <GuideTip variant="warning" title="Insurance information boundary">
        Fixtyer is not FCA-authorised and does not advise on policy terms. Check your policy and
        speak to your insurer before deciding what to notify or claim; in every case, check your
        policy and speak to your insurer about the actual wording.
      </GuideTip>

      <GuideSection title="Questions to ask before choosing" icon={<span aria-hidden="true">?</span>}>
        <ul className="list-disc space-y-3 pl-5">
          <li>
            Does the policy require you to notify the incident even if you do not make an insurance
            claim?
          </li>
          <li>
            What compulsory and voluntary excess would apply, and is the repair cost above it?
          </li>
          <li>
            How could a claim affect your no-claims discount or renewal premium? Protection of a
            no-claims discount does not necessarily prevent the premium changing.
          </li>
          <li>
            Can the insurer arrange repairs sooner, and could it later seek recovery from another
            party?
          </li>
          <li>
            What evidence and deadlines apply to a direct claim against the maintaining authority?
          </li>
        </ul>
      </GuideSection>

      <GuideSection title="Compare the two routes" icon={<span aria-hidden="true">↔</span>}>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-surface-200">
                <th className="p-3">Issue</th>
                <th className="p-3">Highway-authority claim</th>
                <th className="p-3">Motor insurer</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-200">
              <tr>
                <td className="p-3 font-medium">Basis</td>
                <td className="p-3">Alleged legal responsibility for the road defect.</td>
                <td className="p-3">Your contract of insurance and its cover.</td>
              </tr>
              <tr>
                <td className="p-3 font-medium">Immediate cost</td>
                <td className="p-3">No policy excess, but you organise and evidence the loss.</td>
                <td className="p-3">An excess may apply depending on the policy and claim.</td>
              </tr>
              <tr>
                <td className="p-3 font-medium">Outcome</td>
                <td className="p-3">Depends on the facts, evidence and legal response.</td>
                <td className="p-3">Depends on policy cover, terms and the insurer’s handling.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </GuideSection>

      <GuideSection title="Keep both evidence trails usable" icon={<span aria-hidden="true">✓</span>}>
        <p className="mb-4">
          Preserve original photographs, dashcam files, recovery records, garage findings and
          invoices. Do not dispose of damaged parts before checking whether the authority or insurer
          may need to inspect them. Keep a dated note of calls and submissions.
        </p>
        <p>
          If you choose a direct route, find the current official page in the{' '}
          <Link to="/guides/highway-authority-claim-routes" className={inlineLink}>
            claim-routes directory
          </Link>
          . The directory does not replace your policy or the authority&apos;s instructions.
        </p>
      </GuideSection>

      <PhaseFiveGuideNotes />
    </GuideLayout>
  );
}
